import { Check, ChevronsUpDown, LogOut, Monitor, Moon, Sun } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useAccountSummaries } from '@/lib/accounts';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';

/**
 * Sidebar footer: the signed-in user, their teams, and the theme.
 *
 * Personal accounts are not listed: the console operates a dispensary, and a
 * personal account holds none.
 *
 * Rewritten from the block's placeholder — it rendered static demo data and had
 * no way to switch accounts, which staff who belong to more than one dispensary
 * need on every screen.
 */
export function NavUser() {
  const { isMobile } = useSidebar();
  const { session, claims, activeAccountId, setActiveAccountId, signOut } = useAuth();
  const { theme, setTheme } = useTheme();

  const accounts = useAccountSummaries(claims.teamAccountIds);

  const email = session?.user.email ?? '';
  const initials = email.slice(0, 2).toUpperCase() || '??';

  // The id is what the token gives us; the name arrives a query later. Show
  // whichever we have rather than a blank while the lookup is in flight.
  const label = (id: string) => accounts.get(id)?.name ?? id.slice(0, 8);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="size-8 rounded-lg">
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{email}</span>
                <span className="text-muted-foreground truncate text-xs">
                  {activeAccountId ? label(activeAccountId) : 'No workspace'}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            {claims.teamAccountIds.length > 1 && (
              <>
                <DropdownMenuLabel className="text-muted-foreground text-xs">
                  Accounts
                </DropdownMenuLabel>
                <DropdownMenuGroup>
                  {claims.teamAccountIds.map((id) => (
                    <DropdownMenuItem key={id} onClick={() => setActiveAccountId(id)}>
                      <span className="truncate">{label(id)}</span>
                      <span className="text-muted-foreground ml-1 truncate text-xs capitalize">
                        {claims.rolesByAccount[id]}
                      </span>
                      {id === activeAccountId && <Check className="ml-auto size-4" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            )}

            <DropdownMenuLabel className="text-muted-foreground text-xs">Theme</DropdownMenuLabel>
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => setTheme('light')}>
                <Sun />
                Light
                {theme === 'light' && <Check className="ml-auto size-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('dark')}>
                <Moon />
                Dark
                {theme === 'dark' && <Check className="ml-auto size-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('system')}>
                <Monitor />
                System
                {theme === 'system' && <Check className="ml-auto size-4" />}
              </DropdownMenuItem>
            </DropdownMenuGroup>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={() => void signOut()}>
              <LogOut />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
